import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useState } from 'react';
import { Button, Checkbox, EmptyState, Field, Modal, Notice, PageHeader, RowActions, Select, StatusIndicator, Table, Tag } from '@voice/ui';
import { errorText } from '../components/common';
import { api } from '../lib/api';
import styles from './Pages.module.css';

type Day = 'mon' | 'tue' | 'wed' | 'thu' | 'fri' | 'sat' | 'sun';
interface Range {
  from: string;
  to: string;
}
type Weekly = Record<Day, Range[]>;
interface Exception {
  id: string;
  fromDate: string;
  toDate: string;
  name: string;
  closed: boolean;
  hours: Range[];
}
interface Schedule {
  id: string;
  name: string;
  timezone: string;
  state: string | null;
  holidaysClosed: boolean;
  weekly: Weekly;
  override: 'auto' | 'open' | 'closed';
  exceptions: Exception[];
  status: { open: boolean; reason: string; name?: string };
}
interface Response {
  states: { code: string; name: string }[];
  schedules: Schedule[];
}

const DAYS: { key: Day; label: string }[] = [
  { key: 'mon', label: 'Montag' },
  { key: 'tue', label: 'Dienstag' },
  { key: 'wed', label: 'Mittwoch' },
  { key: 'thu', label: 'Donnerstag' },
  { key: 'fri', label: 'Freitag' },
  { key: 'sat', label: 'Samstag' },
  { key: 'sun', label: 'Sonntag' },
];
const KEY = ['schedules'] as const;

function reasonText(s: Schedule['status']): string {
  switch (s.reason) {
    case 'override':
      return 'von Hand umgestellt';
    case 'exception':
      return s.name ?? 'Sondertag';
    case 'holiday':
      return s.name ?? 'Feiertag';
    case 'hours':
      return 'laut Geschäftszeiten';
    default:
      return 'außerhalb der Geschäftszeiten';
  }
}

export function Schedules() {
  const qc = useQueryClient();
  const query = useQuery({ queryKey: KEY, queryFn: () => api<Response>('/schedules'), refetchInterval: 30_000 });
  const [editing, setEditing] = useState<Schedule | 'new' | null>(null);
  const refresh = () => qc.invalidateQueries({ queryKey: KEY });
  const override = useMutation({ mutationFn: (v: { id: string; override: Schedule['override'] }) => api(`/schedules/${v.id}/override`, { method: 'POST', body: { override: v.override } }), onSuccess: refresh });

  return (
    <div className={styles.page}>
      <PageHeader title="Geschäftszeiten">
        <Button onClick={() => setEditing('new')}>Neue Geschäftszeit</Button>
      </PageHeader>
      <p className={styles.note} style={{ marginBottom: 24 }}>
        Geschäftszeiten steuern in Abläufen die Verzweigung „offen / geschlossen“. Feiertage richten sich nach dem Bundesland.
      </p>
      {query.isPending && <p className={styles.note}>Lädt …</p>}
      {query.isError && <Notice tone="error">Die Geschäftszeiten konnten nicht geladen werden.</Notice>}
      {query.data?.schedules.length === 0 && <EmptyState>Noch keine Geschäftszeiten. Lege z. B. „Büro“ mit Montag bis Freitag 08:00–17:00 an.</EmptyState>}
      {query.data?.schedules.map((s) => (
        <section key={s.id} className={styles.section} style={{ marginTop: 28 }}>
          <div className={styles.inline} style={{ justifyContent: 'space-between' }}>
            <div>
              <h2 className={styles.heading} style={{ marginBottom: 4 }}>
                {s.name}
              </h2>
              <StatusIndicator state={s.status.open ? 'ok' : 'busy'} label={`${s.status.open ? 'Geöffnet' : 'Geschlossen'} – ${reasonText(s.status)}`} />
            </div>
            <RowActions>
              {(['auto', 'open', 'closed'] as const).map((o) => (
                <Button key={o} variant={s.override === o ? 'primary' : 'secondary'} onClick={() => override.mutate({ id: s.id, override: o })} aria-pressed={s.override === o}>
                  {o === 'auto' ? 'Automatisch' : o === 'open' ? 'Jetzt offen' : 'Jetzt geschlossen'}
                </Button>
              ))}
              <Button variant="secondary" onClick={() => setEditing(s)}>
                Bearbeiten
              </Button>
            </RowActions>
          </div>
          <Table aria-label={`Zeiten ${s.name}`}>
            <tbody>
              {DAYS.map((d) => (
                <tr key={d.key}>
                  <th scope="row" style={{ width: 140 }}>
                    {d.label}
                  </th>
                  <td>{s.weekly[d.key].length === 0 ? <span className={styles.muted}>geschlossen</span> : s.weekly[d.key].map((r) => `${r.from}–${r.to}`).join(', ')}</td>
                </tr>
              ))}
            </tbody>
          </Table>
          {s.exceptions.length > 0 && (
            <p className={styles.muted} style={{ marginTop: 8 }}>
              Sondertage: {s.exceptions.map((e) => `${e.name} (${e.fromDate === e.toDate ? e.fromDate : `${e.fromDate} bis ${e.toDate}`}${e.closed ? '' : ', verkürzt'})`).join('; ')}
            </p>
          )}
        </section>
      ))}
      {editing && query.data && <ScheduleForm key={editing === 'new' ? 'new' : editing.id} schedule={editing === 'new' ? null : editing} states={query.data.states} onClose={() => setEditing(null)} onChanged={refresh} />}
    </div>
  );
}

const DEFAULT: Weekly = {
  mon: [{ from: '08:00', to: '17:00' }],
  tue: [{ from: '08:00', to: '17:00' }],
  wed: [{ from: '08:00', to: '17:00' }],
  thu: [{ from: '08:00', to: '17:00' }],
  fri: [{ from: '08:00', to: '17:00' }],
  sat: [],
  sun: [],
};

function ScheduleForm({ schedule, states, onClose, onChanged }: { schedule: Schedule | null; states: Response['states']; onClose: () => void; onChanged: () => void }) {
  const [name, setName] = useState(schedule?.name ?? '');
  const [timezone, setTimezone] = useState(schedule?.timezone ?? 'Europe/Berlin');
  const [state, setState] = useState(schedule?.state ?? '');
  const [holidaysClosed, setHolidaysClosed] = useState(schedule?.holidaysClosed ?? true);
  const [weekly, setWeekly] = useState<Weekly>(schedule?.weekly ?? DEFAULT);
  const body = () => ({ name, timezone, state: state || null, holidaysClosed, weekly });
  const save = useMutation({
    mutationFn: () => (schedule ? api(`/schedules/${schedule.id}`, { method: 'PATCH', body: body() }) : api('/schedules', { method: 'POST', body: body() })),
    onSuccess: () => {
      onChanged();
      onClose();
    },
  });
  const remove = useMutation({
    mutationFn: () => api(`/schedules/${schedule!.id}`, { method: 'DELETE' }),
    onSuccess: () => {
      onChanged();
      onClose();
    },
  });
  const setRange = (day: Day, i: number, patch: Partial<Range>) => setWeekly((w) => ({ ...w, [day]: w[day].map((r, j) => (j === i ? { ...r, ...patch } : r)) }));

  return (
    <Modal
      open
      onClose={onClose}
      title={schedule ? `Geschäftszeit „${schedule.name}“` : 'Neue Geschäftszeit'}
      size="lg"
      footer={
        <>
          <Button variant="secondary" onClick={onClose}>
            Abbrechen
          </Button>
          <Button onClick={() => save.mutate()} disabled={!name || save.isPending}>
            Speichern
          </Button>
        </>
      }
    >
      <div className={styles.form}>
        <div className={styles.row}>
          <Field label="Name" value={name} onChange={(e) => setName(e.target.value)} placeholder="Büro" autoFocus />
          <Select label="Bundesland (für Feiertage)" value={state} onChange={(e) => setState(e.target.value)}>
            <option value="">Nur bundesweite Feiertage</option>
            {states.map((s) => (
              <option key={s.code} value={s.code}>
                {s.name}
              </option>
            ))}
          </Select>
        </div>
        <Checkbox label="An Feiertagen geschlossen" checked={holidaysClosed} onChange={(e) => setHolidaysClosed(e.target.checked)} />
        <Field label="Zeitzone" value={timezone} onChange={(e) => setTimezone(e.target.value)} />

        <div>
          <h3 className={styles.heading}>Wochenplan</h3>
          <div className={styles.stack}>
            {DAYS.map((d) => (
              <div key={d.key} className={styles.inline} style={{ alignItems: 'center' }}>
                <span style={{ width: 100 }}>{d.label}</span>
                {weekly[d.key].length === 0 && <span className={styles.muted}>geschlossen</span>}
                {weekly[d.key].map((r, i) => (
                  <span key={i} className={styles.inline}>
                    <input type="time" aria-label={`${d.label} von`} value={r.from} onChange={(e) => setRange(d.key, i, { from: e.target.value })} className={styles.time} />
                    –
                    <input type="time" aria-label={`${d.label} bis`} value={r.to === '24:00' ? '23:59' : r.to} onChange={(e) => setRange(d.key, i, { to: e.target.value })} className={styles.time} />
                    <Button variant="secondary" onClick={() => setWeekly((w) => ({ ...w, [d.key]: w[d.key].filter((_, j) => j !== i) }))} aria-label="Zeitfenster entfernen">
                      ×
                    </Button>
                  </span>
                ))}
                <Button variant="secondary" onClick={() => setWeekly((w) => ({ ...w, [d.key]: [...w[d.key], { from: '08:00', to: '17:00' }] }))}>
                  + Zeitfenster
                </Button>
              </div>
            ))}
          </div>
        </div>
        {save.isError && <Notice tone="error">{errorText(save.error)}</Notice>}

        {schedule && <Exceptions schedule={schedule} onChanged={onChanged} />}
        {schedule && (
          <div>
            <Button variant="secondary" onClick={() => confirm('Geschäftszeit löschen? Abläufe, die sie nutzen, funktionieren danach nicht mehr.') && remove.mutate()}>
              Geschäftszeit löschen
            </Button>
          </div>
        )}
      </div>
    </Modal>
  );
}

function Exceptions({ schedule, onChanged }: { schedule: Schedule; onChanged: () => void }) {
  const today = new Date().toISOString().slice(0, 10);
  const [name, setName] = useState('');
  const [from, setFrom] = useState(today);
  const [to, setTo] = useState(today);
  const [closed, setClosed] = useState(true);
  const [hours, setHours] = useState<Range>({ from: '09:00', to: '12:00' });
  const add = useMutation({
    mutationFn: () => api(`/schedules/${schedule.id}/exceptions`, { method: 'POST', body: { name, fromDate: from, toDate: to < from ? from : to, closed, hours: closed ? [] : [hours] } }),
    onSuccess: () => {
      setName('');
      onChanged();
    },
  });
  const remove = useMutation({ mutationFn: (id: string) => api(`/exceptions/${id}`, { method: 'DELETE' }), onSuccess: onChanged });
  return (
    <div>
      <h3 className={styles.heading}>Sondertage und Betriebsferien</h3>
      {schedule.exceptions.length === 0 ? (
        <p className={styles.muted}>Keine Sondertage.</p>
      ) : (
        <div className={styles.deviceList}>
          {schedule.exceptions.map((e) => (
            <div key={e.id} className={styles.device}>
              <div className={styles.deviceInfo}>
                <span className={styles.deviceName}>{e.name}</span>
                <span className={styles.muted}>
                  {e.fromDate === e.toDate ? e.fromDate : `${e.fromDate} bis ${e.toDate}`} · {e.closed ? 'geschlossen' : e.hours.map((h) => `${h.from}–${h.to}`).join(', ')}
                </span>
              </div>
              <Button variant="secondary" onClick={() => remove.mutate(e.id)}>
                Entfernen
              </Button>
            </div>
          ))}
        </div>
      )}
      <div className={styles.row} style={{ marginTop: 16 }}>
        <Field label="Bezeichnung" value={name} onChange={(e) => setName(e.target.value)} placeholder="Betriebsferien" />
        <Select label="An diesen Tagen" value={closed ? 'closed' : 'short'} onChange={(e) => setClosed(e.target.value === 'closed')}>
          <option value="closed">ganztägig geschlossen</option>
          <option value="short">abweichende Zeiten</option>
        </Select>
        <Field label="Von" type="date" value={from} onChange={(e) => setFrom(e.target.value)} />
        <Field label="Bis" type="date" value={to} min={from} onChange={(e) => setTo(e.target.value)} />
      </div>
      {!closed && (
        <div className={styles.inline} style={{ marginTop: 8 }}>
          <input type="time" aria-label="Von" value={hours.from} onChange={(e) => setHours({ ...hours, from: e.target.value })} className={styles.time} />–
          <input type="time" aria-label="Bis" value={hours.to} onChange={(e) => setHours({ ...hours, to: e.target.value })} className={styles.time} />
        </div>
      )}
      {add.isError && <Notice tone="error">{errorText(add.error)}</Notice>}
      <div style={{ marginTop: 12 }}>
        <Button variant="secondary" onClick={() => add.mutate()} disabled={!name || add.isPending}>
          Sondertag hinzufügen
        </Button>
      </div>
      <Tag>Gilt vor den Wochenzeiten und den Feiertagen.</Tag>
    </div>
  );
}
